import { ReactNode, useContext, useEffect, useState } from "react";
import classNames from "classnames";

import Icon from "../../icon";
import { TreeContext } from "./treeContext";
import { Remove, Add } from "@mui/icons-material";

type MotifTreeNodeProps = {
  children?: React.ReactNode;
  value?: React.ReactNode;
  open?: boolean;
  onClick?: () => void;
  defaultExpandAll?: boolean;
  className?: string;
  disabled?: boolean;
  icons?: {
    close?: ReactNode | null;
    open?: ReactNode | null;
    add?: ReactNode | null;
    remove?: ReactNode | null;
  };
};

export const MotifTreeNode: React.FC<MotifTreeNodeProps> = ({
  value,
  open = false,
  onClick,
  children,
  className = "",
  disabled = false,
  icons: userIcons = {}
}: MotifTreeNodeProps) => {
  const {
    defaultExpandAll,
    expandAll,
    showLines,
    disabled: disabledTree
  } = useContext(TreeContext);
  const [showList, setShowList] = useState(defaultExpandAll);
  const [visible, setVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    setVisible(!visible);
    if (onClick) {
      onClick();
    }
  };

  useEffect(() => {
    setVisible(expandAll || showList || open);
  }, [expandAll, open]);

  useEffect(() => {
    setShowList(false);
  }, []);

  const handleMouseOver = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const classes = classNames({
    "motif-tree-node": true,
    "motif-tree-node-visible": visible,
    "motif-tree-node-lines": showLines,
    [className]: true
  });

  const contentClasses = classNames({
    "motif-tree-node-content": true,
    "motif-tree-node-content-hovered": isHovered && !disabled && !disabledTree
  });

  const buttonExpandCollapse = children ? (
    <Icon
      className="motif-tree-node-button"
      aria-label={visible ? "expand tree" : "collapse tree"}
      onClick={handleClick}
      disabled={disabled || disabledTree}
      src={visible ? userIcons.close : userIcons.open}
    />
  ) : null;

  return (
    <li className={classes}>
      <div
        className={contentClasses}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
      >
        {buttonExpandCollapse}
        <p className="motif-tree-node-value">{value}</p>
      </div>

      {children && <ul className="motif-tree-nodes">{children}</ul>}
    </li>
  );
};
